import APIService from "./APIService";

/**
 * Service for handling consent-related API operations
 */
const ConsentAPI = {
  /**
   * Verify a consent verification key
   * @param {string} verificationKey - The key to verify
   * @returns {Promise} - Promise that resolves with the verification result
   */
  verifyConsentKey: async (verificationKey) => {
    try {
      // Use GET request to verify the key
      const response = await APIService.get(
        `/api/v1/base/consents/verification/${encodeURIComponent(
          verificationKey,
        )}`,
      );

      console.log(`[ConsentAPI] Verification key response:`, response);

      // Handle different response formats
      if (response) {
        // If response has a data property, use it; otherwise use the response itself
        return response.data || response;
      }

      // If response is null/undefined, return a failed verification object
      return {
        verified: false,
        success: false,
        message: "No response received from server",
      };
    } catch (error) {
      console.error("[ConsentAPI] Error verifying consent key:", error);
      throw error;
    }
  },
  /**
   * Resend verification email to a user (generates new key, deletes old ones, sends email)
   * @param {string} userUuid - User UUID to resend verification for
   * @param {string} email - User's email address
   * @returns {Promise} - Promise that resolves with the resend result
   */
  resendVerificationEmail: async (userUuid, email) => {
    try {
      const response = await APIService.post(
        "/api/v1/base/consents/resend-verification",
        {
          userUuid,
          email,
          timestamp: new Date().toISOString(),
        },
      );

      console.log(`[ConsentAPI] Resend verification response:`, response);
      return response.data || response;
    } catch (error) {
      console.error("[ConsentAPI] Error resending verification email:", error);
      throw error;
    }
  },

  // =====================================================================
  // Owner Read-Only Access Consent
  // =====================================================================

  /**
   * Get current member's owner-access consent status
   * @returns {Promise<{dashboardAccess: boolean, analysisAccess: boolean, reportsAccess: boolean}>}
   */
  getMyOwnerAccessConsent: async () => {
    try {
      const response = await APIService.get(
        "/api/v1/base/consents/owner-access/my-consent",
      );
      return response.data || response;
    } catch (error) {
      console.error("[ConsentAPI] Error getting owner access consent:", error);
      throw error;
    }
  },

  /**
   * Update owner-access consent (member grants or revokes)
   * @param {string} consentType - "owner_dashboard_access", "owner_analysis_access", or "owner_reports_access"
   * @param {boolean} granted - true to grant, false to revoke
   * @returns {Promise}
   */
  updateOwnerAccessConsent: async (consentType, granted) => {
    try {
      const response = await APIService.put(
        "/api/v1/base/consents/owner-access/update",
        { consentType, granted },
      );
      return response.data || response;
    } catch (error) {
      console.error("[ConsentAPI] Error updating owner access consent:", error);
      throw error;
    }
  },

  /**
   * Get list of members with their consent status (for account owner)
   * @returns {Promise<Array<{uuid, firstName, lastName, email, dashboardAccess, analysisAccess, reportsAccess}>>}
   */
  getConsentedMembers: async () => {
    try {
      const response = await APIService.get(
        "/api/v1/base/consents/owner-access/consented-members",
      );
      return response.data || response;
    } catch (error) {
      console.error("[ConsentAPI] Error getting consented members:", error);
      throw error;
    }
  },

  /**
   * Check if a specific member has granted consent (for account owner)
   * @param {string} memberUuid
   * @returns {Promise<{dashboardAccess: boolean, analysisAccess: boolean}>}
   */
  checkMemberConsent: async (memberUuid) => {
    try {
      const response = await APIService.get(
        `/api/v1/base/consents/owner-access/check/${encodeURIComponent(
          memberUuid,
        )}`,
      );
      return response.data || response;
    } catch (error) {
      console.error("[ConsentAPI] Error checking member consent:", error);
      throw error;
    }
  },
};

export default ConsentAPI;
